import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { deleteMediaDraft, mediaDraftKey, readMediaDraft, writeMediaDraft } from './drafts';
import { newMediaId, retireDraftIdentity } from './draftIdentity';
import { deleteMedia, uploadRecording } from './client';
import { RECORDING_LIMITS } from './recorder';

export const MEDIA_DRAFT_LOAD_TIMEOUT = 8000;

// A moderation rejection keeps placement metadata only, including on disk and
// in Undo's detached attachments. A stale local Blob must not bypass the ban.
const withoutRejectedSource = item => item.status === 'rejected' ? { ...item,
  blob: undefined, playback_url: undefined, poster_url: undefined, waveform_url: undefined, error: null } : item;

const validate = items => {
  const counts = { audio: 0, video: 0 };
  const ids = new Set();
  return items.map(source => {
    const item = withoutRejectedSource(source);
    const limit = RECORDING_LIMITS[item.kind];
    const valid = limit && ++counts[item.kind] <= limit.count
      && (!item.id || !ids.has(item.id))
      && (item.duration_ms == null ? !item.blob : Number.isFinite(item.duration_ms)
        && item.duration_ms > 0 && item.duration_ms <= limit.seconds * 1000)
      && (!item.blob || item.blob.size > 0);
    if (item.id) ids.add(item.id);
    return { ...item, localId: item.localId || newMediaId(),
      ...(valid ? item.error === 'MEDIA_DRAFT_LIMIT' ? { error: null } : {} : { error: 'MEDIA_DRAFT_LIMIT' }) };
  });
};

export default function useMediaDraft({ userId, storyId, draftId, savedMedia, caps }) {
  const [, render] = useReducer(value => value + 1, 0);
  const [loadAttempt, retry] = useReducer(value => value + 1, 0);
  const owner = useMemo(() => ({ key: mediaDraftKey(userId, storyId, draftId), userId,
    storyId, draftId, items: [], baseline: null, changed: false, ready: false, storageError: false, loadError: false,
    writes: Promise.resolve(), jobs: new Map(), removed: [], detached: [], cleared: false }), [userId, storyId, draftId]);
  // API metadata is JSON. Compare its contents so equivalent arrays from a
  // rerender do not repeat hydration, change local IDs, or cause refresh loops.
  const savedMediaKey = JSON.stringify(savedMedia ?? null);
  const currentOwner = useRef(owner);
  currentOwner.current = owner;
  const active = useRef(false);
  const refresh = useCallback(() => {
    if (active.current && currentOwner.current === owner) render();
  }, [owner]);
  const persist = useCallback(() => {
    if (owner.cleared) return owner.writes;
    const snapshot = { key: owner.key, userId, storyId, draftId, changed: owner.changed, baseline: owner.baseline,
      removed: owner.removed, detached: owner.detached, items: owner.items.map(({ progress, ...item }) => item) };
    owner.writes = owner.writes.then(() => writeMediaDraft(snapshot)).catch(() => {
      owner.storageError = true;
      refresh();
    });
    return owner.writes;
  }, [owner, userId, storyId, draftId, refresh]);

  useEffect(() => {
    active.current = true;
    let cancelled = false;
    let settled = false;
    const failed = () => {
      if (cancelled || settled || owner.cleared) return;
      settled = true;
      clearTimeout(timer);
      // An unread draft may contain unsent recordings. Keep Send blocked and
      // offer an explicit retry instead of treating an unavailable DB as empty.
      owner.loadError = true;
      owner.ready = false;
      refresh();
    };
    const timer = setTimeout(failed, MEDIA_DRAFT_LOAD_TIMEOUT);
    Promise.resolve().then(() => readMediaDraft(owner.key)).then(saved => {
      if (cancelled || settled || owner.cleared) return;
      if (saved && saved.userId === userId) {
        owner.items = validate(saved.items || []);
        owner.changed = saved.changed === true;
        owner.baseline = Array.isArray(saved.baseline) ? saved.baseline : null;
        owner.removed = Array.isArray(saved.removed) ? saved.removed : [];
        owner.detached = Array.isArray(saved.detached) ? saved.detached.map(withoutRejectedSource) : [];
        if ([...(saved.items || []), ...(saved.detached || [])].some(item => item.status === 'rejected' && item.blob)) persist();
      }
      settled = true;
      clearTimeout(timer);
      owner.loadError = false;
      owner.ready = true;
      refresh();
    }).catch(failed);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      active.current = false;
      owner.jobs.forEach(job => job.abort());
      owner.jobs.clear();
    };
  }, [owner, userId, refresh, persist, loadAttempt]);

  const retryLoad = useCallback(() => {
    if (!owner.loadError || owner.cleared) return;
    owner.loadError = false;
    owner.ready = false;
    retry();
  }, [owner]);

  useEffect(() => {
    if (!owner.ready || owner.savedMediaKey === savedMediaKey) return;
    if (savedMedia && owner.baseline === null) owner.baseline = savedMedia.map(item => item.id);
    if (!owner.changed && savedMedia) {
      owner.items = validate(savedMedia.map(item => ({ ...item, persisted: true })));
    } else if (savedMedia) {
      // Preserve local editing while applying an authoritative rejection that
      // arrived after this draft was last stored.
      const rejected = new Map(savedMedia.filter(item => item.status === 'rejected').map(item => [item.id, item]));
      const sanitize = item => {
        if (!rejected.has(item.id)) return item;
        // A poll started before moderation may still resolve with stale ready
        // metadata. Retire its controller before applying the rejection so it
        // cannot restore playback URLs or clear the placeholder afterward.
        owner.jobs.get(item.localId)?.abort();
        owner.jobs.delete(item.localId);
        return withoutRejectedSource({ ...item, ...rejected.get(item.id), localId: item.localId });
      };
      if (owner.items.some(item => rejected.has(item.id)) || owner.detached.some(item => rejected.has(item.id))) {
        owner.items = owner.items.map(sanitize);
        owner.detached = owner.detached.map(sanitize);
        persist();
      }
    }
    owner.savedMediaKey = savedMediaKey;
    refresh();
  }, [owner, owner.ready, savedMedia, savedMediaKey, refresh, persist]);

  const add = useCallback((recording, replaceLocalId, { autoUpload = false } = {}) => {
    if (owner.cleared) return;
    const old = owner.items.find(item => item.localId === replaceLocalId);
    const count = owner.items.filter(item => item.kind === recording.kind && item !== old).length;
    if (count >= RECORDING_LIMITS[recording.kind].count) return;
    // Failed/rejected drafts no longer reserve a logical slot on the server.
    // When rerecording a replacement, retain its original published target.
    const occupied = new Set(owner.items.flatMap(item => [item.id, item.replaces_id]));
    const removed = owner.removed.find(item => item.kind === recording.kind && !occupied.has(item.id));
    const replacesId = old ? (['failed', 'rejected', 'cancelled'].includes(old.status)
      ? old.replaces_id : old.id || old.replaces_id) : removed?.id;
    const item = { ...recording, localId: newMediaId(), status: 'local',
      autoUpload,
      ...(replacesId ? { replaces_id: replacesId } : {}) };
    if (old) {
      owner.jobs.get(old.localId)?.abort();
      if (old.persisted && !owner.removed.some(value => value.id === old.id)) {
        owner.removed = [...owner.removed, { id: old.id, kind: old.kind }];
      }
      owner.items = owner.items.map(existing => existing === old ? item : existing);
    } else owner.items = [...owner.items, item];
    owner.changed = true;
    refresh();
    persist();
    return item;
  }, [owner, refresh, persist]);

  // Text history owns recording placement. Keep removed takes until this
  // draft is saved so Undo can restore both the node and its actual recording.
  const syncMedia = useCallback(keys => {
    if (!owner.ready || owner.cleared) return;
    const present = item => keys.includes(item.localId) || (item.id && keys.includes(item.id));
    const removed = owner.items.filter(item => !present(item));
    const restored = owner.detached.filter(present);
    if (!removed.length && !restored.length) return;
    removed.forEach(item => {
      owner.jobs.get(item.localId)?.abort();
      owner.jobs.delete(item.localId);
      if (item.persisted && !owner.removed.some(value => value.id === item.id)) {
        owner.removed = [...owner.removed, { id: item.id, kind: item.kind }];
      }
    });
    owner.detached = [...owner.detached.filter(item => !present(item)), ...removed];
    owner.items = validate([...owner.items.filter(item => !removed.includes(item)), ...restored]);
    owner.changed = true;
    persist();
    refresh();
  }, [owner, persist, refresh]);

  const remove = reference => {
    const item = owner.items.find(value => value.localId === reference.localId);
    if (!item || owner.cleared) return;
    owner.jobs.get(item.localId)?.abort();
    if (item.persisted && !owner.removed.some(value => value.id === item.id)) {
      owner.removed = [...owner.removed, { id: item.id, kind: item.kind }];
    }
    owner.items = validate(owner.items.filter(existing => existing.localId !== item.localId));
    owner.changed = true;
    persist();
    refresh();
    if (item.id && !item.persisted) deleteMedia(item.id).catch(() => {});
  };

  const upload = useCallback(async reference => {
    const item = owner.items.find(value => value.localId === reference.localId);
    if (!active.current || currentOwner.current !== owner || owner.cleared || !item
      || item.status === 'ready' || item.status === 'rejected' || item.error === 'MEDIA_DRAFT_LIMIT'
      || owner.jobs.has(item.localId) || (!item.id && !caps[item.kind]?.enabled)) return;
    const controller = new AbortController();
    owner.jobs.set(item.localId, controller);
    const update = async patch => {
      if (controller.signal.aborted || !owner.items.some(value => value.localId === item.localId)) return;
      owner.items = owner.items.map(value => value.localId === item.localId
        ? withoutRejectedSource({ ...value, ...patch, error: null }) : value);
      refresh();
      if ('status' in patch || 'id' in patch) await persist();
    };
    await update({ autoUpload: true });
    try {
      // Keep the Blob durable before starting a request. Removing the take or
      // leaving the editor while IndexedDB writes must not start a late upload.
      await persist();
      if (controller.signal.aborted || !active.current || currentOwner.current !== owner || owner.cleared) return;
      let input = item;
      if (item.blob && ['failed', 'cancelled'].includes(item.status)) {
        if (!caps[item.kind]?.enabled) throw new Error('MEDIA_UPLOAD_UNAVAILABLE');
        if (item.id) await deleteMedia(item.id, controller.signal);
        input = { ...item, id: undefined, status: 'local' };
        await update({ id: undefined, status: 'local' });
      }
      await uploadRecording(input, { storyId, draftId, caps, signal: controller.signal, update });
    } catch (error) {
      if (!controller.signal.aborted) {
        owner.items = owner.items.map(value => value.localId === item.localId
          ? error.name === 'AbortError'
            ? { ...value, autoUpload: false, error: undefined }
            : withoutRejectedSource({ ...value, error: error.response?.data?.message || error.message || 'MEDIA_UPLOAD_FAILED' }) : value);
        await persist();
      }
    } finally {
      if (owner.jobs.get(item.localId) === controller) owner.jobs.delete(item.localId);
      refresh();
    }
  }, [owner, caps, storyId, draftId, refresh, persist]);

  useEffect(() => {
    if (!owner.ready || owner.cleared) return;
    owner.items.forEach(item => {
      // Completing a take authorizes automatic upload. Recorder recovery on
      // unmount keeps an unconfirmed local take without starting any requests.
      if (item.autoUpload && !item.error
        && ['local', 'uploading', 'queued', 'processing'].includes(item.status)) upload(item);
    });
  }, [owner, owner.ready, owner.items, upload]);

  const clear = async () => {
    owner.cleared = true;
    owner.jobs.forEach(job => job.abort());
    retireDraftIdentity(userId, storyId, draftId);
    // Deletion stays ordered after this generation's pending writes, but no
    // IndexedDB callback may hold an already accepted server action on screen.
    void owner.writes.then(() => deleteMediaDraft(owner.key)).catch(() => {});
    owner.items = [];
    owner.detached = [];
    refresh();
  };
  // Explicit conflict recovery keeps new local takes, then lets the author
  // review the current server attachments before submitting again.
  const rebase = async latest => {
    const previous = new Set(owner.baseline || []);
    const serverIds = new Set(latest.map(item => item.id));
    const additions = owner.items.filter(item => !item.id || (!previous.has(item.id) && !serverIds.has(item.id)));
    owner.baseline = latest.map(item => item.id);
    owner.removed = [];
    owner.items = validate([...latest.map(item => ({ ...item, persisted: true })), ...additions]);
    owner.changed = true;
    await persist();
    refresh();
  };
  // IndexedDB may finish before the saved opinion arrives. Do not expose an
  // enabled Send button with an earlier (possibly empty) attachment registry.
  const ready = owner.ready && owner.savedMediaKey === savedMediaKey;
  return { items: owner.items, changed: owner.changed, ready,
    loadError: owner.loadError, retryLoad,
    storageError: owner.storageError && owner.changed,
    pending: !ready || owner.items.some(item => !['ready', 'rejected'].includes(item.status)
      || (item.status === 'rejected' ? !item.id : item.error)),
    mediaIds: owner.items.map(item => item.id), baseline: owner.baseline,
    uploading: item => owner.jobs.has(item.localId), add, remove, upload, clear, rebase, syncMedia,
    removeAll: () => { owner.items.forEach(remove); },
  };
}
